import { daysPerYear, type CalendarSystem } from '@realmery/realm-core';

/** The extra space before an event, to scale, and a marker across a gap too long to draw. */
export interface Gap {
  readonly px: number;
  readonly marker: string | null;
}

/** The most space drawn between two events; a longer gap is marked instead. */
const MAX_GAP = 160;
/** The space the typical (median) gap is drawn with. */
const TYPICAL_GAP = 28;

/**
 * Gaps to scale between events in date order (docs/CHRONOLOGY-CALENDAR-REDESIGN-PLAN.md
 * slice 2): the typical gap gets a little space, others in proportion, up to a cap; past it,
 * the gap is marked with how long it is - in years and days with an active calendar, where
 * a sort key counts days, or in sort-key steps without one.
 */
export function scaleGaps(keys: readonly bigint[], calendar: CalendarSystem | null): Gap[] {
  const deltas = keys.map((k, i) => (i === 0 ? 0 : Number(k - keys[i - 1]!)));
  const positive = deltas.slice(1).filter((d) => d > 0).sort((a, b) => a - b);
  const median = positive.length === 0 ? 1 : positive[Math.floor(positive.length / 2)]!;
  return deltas.map((d, i) => {
    if (i === 0 || d <= 0) return { px: 0, marker: null };
    const px = (d / median) * TYPICAL_GAP;
    return px > MAX_GAP ? { px: MAX_GAP, marker: `⋯ ${duration(d, calendar)} ⋯` } : { px: Math.round(px), marker: null };
  });
}

function duration(steps: number, calendar: CalendarSystem | null): string {
  if (!calendar) return `${steps.toLocaleString('en')} on the sort key`;
  const perYear = daysPerYear(calendar);
  const years = perYear > 0 ? steps / perYear : 0;
  if (years >= 1) {
    const whole = Math.round(years);
    return `${whole.toLocaleString('en')} ${whole === 1 ? 'year' : 'years'}`;
  }
  return `${steps.toLocaleString('en')} ${steps === 1 ? 'day' : 'days'}`;
}

